import { H1, H2, H3, P, TextBlock, H4 } from "@cd2/text"
import React from "react"
import { IComponentProps, IComponentProp } from "../types/utils"
import { MultiButton } from "./MultiButton"
import { Spacer } from "@cd2/components/build/es6"

interface ITitleProps {
  pre?: string
  title: string | React.ReactNode
  prompt?: React.ReactNode
}
export function Title({ pre, title, prompt }: ITitleProps) {
  return <TextGroupLarge pre={pre} heading={title} body={prompt ? <P>{prompt}</P> : undefined} />
}

interface ITextGroupProps {
  pre?: React.ReactNode
  heading: React.ReactNode
  body?: React.ReactNode
  theme?: {
    pre?: { color?: string }
    heading?: Partial<IComponentProps<typeof H1>>
    body?: { color?: string }
    textBlock?: Partial<IComponentProps<typeof TextBlock>>
  }
}
export function TextGroupLarge({ pre, heading, body, theme }: ITextGroupProps) {
  return (
    <TextBlock blockSpacing={0.25} {...(theme?.textBlock ?? {})}>
      {pre && (
        <H3 weight={400} color={theme?.pre?.color ?? "secondary"}>
          {pre}
        </H3>
      )}
      <H1 {...theme?.heading}>{heading}</H1>
      {body && typeof body === "string" ? (
        <P color={theme?.body?.color ?? "primary"} blockSpacing={0} size="large">
          {body}
        </P>
      ) : (
        body
      )}
    </TextBlock>
  )
}

export function TextGroupMedium({ pre, heading, body, theme }: ITextGroupProps) {
  return (
    <TextBlock blockSpacing={0.125} {...(theme?.textBlock ?? {})}>
      {pre && (
        <H4 weight={400} color={theme?.pre?.color ?? "secondary"}>
          {pre}
        </H4>
      )}
      <H2 color={theme?.heading?.color ?? "default"}>{heading}</H2>
      {body && typeof body === "string" ? (
        <P color={theme?.body?.color ?? "primary"} blockSpacing={0}>
          {body}
        </P>
      ) : (
        body
      )}
    </TextBlock>
  )
}

export function TextGroupSmall({ pre, heading, body, theme }: ITextGroupProps) {
  return (
    <TextBlock blockSpacing={0}>
      {pre && (
        <H4 weight={400} color={"secondary"} size="small" lineHeight={1.25} {...theme?.textBlock}>
          {pre}
        </H4>
      )}
      <H3 color={theme?.heading?.color ?? "default"} weight={400} {...theme?.heading}>
        {heading}
      </H3>
      {body && typeof body === "string" ? (
        <P color={theme?.body?.color ?? "primary"} size="small">
          {body}
        </P>
      ) : (
        body
      )}
    </TextBlock>
  )
}

interface IProps extends ITextGroupProps {
  size: "small" | "medium" | "large"
  actions: IComponentProp<typeof MultiButton, "buttons">
  verticalActions?: boolean
}
export function TextGroupWithActions({
  pre,
  heading,
  body,
  theme,
  size,
  actions,
  verticalActions,
}: IProps) {
  const comps = {
    small: TextGroupSmall,
    medium: TextGroupMedium,
    large: TextGroupLarge,
  }
  const Comp = comps[size]
  return (
    <div>
      <Comp pre={pre} heading={heading} body={body} theme={theme} />
      <Spacer size={16} />
      <MultiButton
        buttons={actions}
        justifyContent={theme?.textBlock?.align === "center" ? "center" : undefined}
        asGroup={verticalActions ? "vertical" : undefined}
      />
    </div>
  )
}
